import React, { useCallback, useEffect, useState } from "react";
import { Users, TrendingUp, Trophy, Calendar as CalIcon, Loader2, XCircle, Bell, AlertTriangle, RefreshCw } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { GrabADealPanel } from "./GrabADeal";
import { useAuth, useCurrency } from "../lib/contexts";
import { STAGE_COLORS, STAGES } from "../lib/permissions";
import { fmtDate } from "../lib/permissions";
import { Card } from "../components/ui/card";
import TeamRightNow from "../components/activity/TeamRightNow";
import MyActivityCard from "../components/activity/MyActivityCard";

const Stat = ({ label, value, icon: Icon, tint, onClick, className = "", testid }) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick}
      data-testid={testid || `stat-${label.replace(/\s+/g, '-').toLowerCase()}`}
      className={`text-left w-full bg-white rounded-md border border-border p-5 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] ${onClick ? "transition-colors" : ""} ${className}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-2 font-heading text-3xl font-bold">{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${tint}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Tag>
  );
};

const AUTO_REFRESH_MS = 15 * 60 * 1000;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const { format } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get("/dashboard/summary");
    setData(r.data);
    setLastSync(new Date());
  }, []);

  const refreshNow = useCallback(() => {
    setSyncing(true);
    load().catch(() => {}).finally(() => setSyncing(false));
  }, [load]);

  useEffect(() => { load(); }, [load]);

  // Stats stay current on a screen people leave open all day.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") refreshNow(); };
    const id = setInterval(refresh, AUTO_REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", refresh); };
  }, [refreshNow]);

  if (!data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const stageData = STAGES.map((s) => ({ stage: s, count: data.by_stage[s] || 0 }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Live snapshot of Sun Nutrafoods sales activity.</p>
        </div>
        <button type="button" data-testid="refresh-dashboard" onClick={refreshNow}
          title="Refreshes automatically every 15 minutes — click to refresh now"
          className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary transition-colors pt-1">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
          <span data-testid="dashboard-last-sync">
            {syncing ? "Refreshing…" : lastSync ? `Updated ${lastSync.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—"}
          </span>
        </button>
      </div>

      {user?.role === "manager" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <GrabADealPanel />
          <TeamRightNow />
        </div>
      ) : user?.role === "staff" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <GrabADealPanel />
          <MyActivityCard />
        </div>
      ) : (
        <GrabADealPanel />
      )}

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-none xl:grid-flow-col xl:auto-cols-fr" data-testid="stat-row">
        <Stat label="Total Contacts" value={data.total_contacts} icon={Users} tint="bg-primary/10 text-primary" />
        <Stat label="Total Deals" value={data.total_deals} icon={TrendingUp} tint="bg-accent/10 text-accent" />
        <Stat label="Deals Won" value={data.by_stage["Won"] || 0} icon={Trophy} tint="bg-green-100 text-green-800" />
        <Stat label="Deals Lost" value={data.lost_total || 0} icon={XCircle} tint="bg-red-100 text-red-800" />
        <Stat label="Closing This Month" value={data.closing_this_month} icon={CalIcon} tint="bg-blue-100 text-blue-800" />
        {data.follow_ups_overdue > 0 && (
          <Stat label="Overdue Follow-ups" value={data.follow_ups_overdue} icon={AlertTriangle}
            testid="overdue-chip" onClick={() => navigate("/deals")}
            tint="bg-red-200 text-red-800" className="border-2 border-red-200 bg-red-50 hover:bg-red-100" />
        )}
        {data.follow_ups_today > 0 && (
          <Stat label="Follow-ups Due Today" value={data.follow_ups_today} icon={Bell}
            testid="today-chip" onClick={() => navigate("/deals")}
            tint="bg-amber-200 text-amber-900" className="border-2 border-amber-200 bg-amber-50 hover:bg-amber-100" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5">
          <h3 className="font-heading font-semibold text-lg">Monthly Won Revenue Trend</h3>
          <p className="text-xs text-muted-foreground mb-4">Actual value of Won deals — last 6 months</p>
          <div className="h-64 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%" minHeight={200} debounce={50}>
              <LineChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E2D8" />
                <XAxis dataKey="month" stroke="#666" fontSize={11} />
                <YAxis stroke="#666" fontSize={11} tickFormatter={(v) => format(v).replace(/[₹$]/, '')} />
                <Tooltip formatter={(v) => format(v)} contentStyle={{ background: 'white', border: '1px solid #E2E2D8', borderRadius: 6 }} />
                <Line type="monotone" dataKey="value" stroke="#355E3B" strokeWidth={2.5} dot={{ r: 4, fill: '#355E3B' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-heading font-semibold text-lg">Deals by Stage</h3>
          <p className="text-xs text-muted-foreground mb-4">Pipeline distribution</p>
          <div className="space-y-2.5" data-testid="stage-breakdown">
            {stageData.map((s) => (
              <div key={s.stage} className="flex items-center justify-between">
                <span className={`text-xs px-2 py-1 rounded border ${STAGE_COLORS[s.stage]}`}>{s.stage}</span>
                <span className="font-semibold tabular-nums">{s.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="font-heading font-semibold text-lg">Staff Activity — Last 7 Days</h3>
        <p className="text-xs text-muted-foreground mb-4">New records created by each sales staff</p>
        <div className="h-56 w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%" minHeight={180} debounce={50}>
            <BarChart data={data.staff_activity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E2D8" />
              <XAxis dataKey="name" stroke="#666" fontSize={11} />
              <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'white', border: '1px solid #E2E2D8', borderRadius: 6 }} />
              <Bar dataKey="contacts" fill="#355E3B" radius={[4,4,0,0]} name="Contacts" />
              <Bar dataKey="deals" fill="#CC5500" radius={[4,4,0,0]} name="Deals" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-4" data-testid="lost-widget">
        <div className="flex items-center justify-between mb-2 gap-2">
          <div>
            <h3 className="font-heading font-semibold text-base flex items-center gap-2">
              <XCircle className="w-4 h-4 text-destructive" />
              Recent Lost Deals
            </h3>
            <p className="text-[11px] text-muted-foreground">Last 5 losses — read the reason, learn, improve.</p>
          </div>
          <div className="text-right">
            <div className="font-heading text-xl font-bold text-destructive">{data.lost_total || 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Lost</div>
          </div>
        </div>
        {(data.lost_recent || []).length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed border-border rounded-md">
            No lost deals yet — keep it up!
          </div>
        ) : (
          <div className="divide-y divide-destructive/15 border-l-4 border-destructive bg-destructive/5 rounded-r-md">
            {data.lost_recent.map((d) => (
              <button key={d.id}
                onClick={() => navigate("/deals")}
                data-testid={`lost-item-${d.id}`}
                className="w-full text-left px-3 py-2 hover:bg-destructive/10 transition-colors">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-sm min-w-0 truncate">
                    <span className="font-semibold">{d.product_name || "—"}</span>
                    <span className="text-xs text-muted-foreground"> · {d.contact_name} · {d.created_by_name || "—"} · Est. {format(d.estimated_value)}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground shrink-0">{fmtDate(d.closed_at)}</div>
                </div>
                {d.lost_reason && (
                  <div className="text-[11px] text-neutral-700 truncate">{d.lost_reason}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
